/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { highlightStyles } from './HighlightTextStyles.css';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';

type Props = TestIdProps & {
  /**
   * The text string to apply highlighting to.
   */
  children?: string;
  /**
   * The text string to match.
   */
  matchText?: string;
};

const HIGHLIGHT_TOKEN = '__@@__';
const HIGHLIGHT_PATTERN = `${HIGHLIGHT_TOKEN}$&${HIGHLIGHT_TOKEN}`;

/**
 * Renders a text string with highlighting applied to the given text to match.
 */
export function HighlightText({ children = '', matchText = '', testId }: Props) {
  const testIdProps = useTestId(testId);
  if (matchText.length > 0 && children.length > 0) {
    const escapedMatchText = escapeRegExp(matchText);
    const highlightedText = children.replace(new RegExp(escapedMatchText, 'gi'), HIGHLIGHT_PATTERN);
    const tokens = highlightedText.split(HIGHLIGHT_TOKEN);
    const nodes = tokens.map((current, index) =>
      index % 2 == 0 ? current : <span class={highlightStyles}>{current}</span>
    );

    return <span {...testIdProps}>{nodes}</span>;
  }
  return <span {...testIdProps}>{children}</span>;
}

function escapeRegExp(str: string) {
  // copied from:
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping
  return str.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}
